import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../services/auth';

interface ProviderInfo {
  id: string;
  label: string;
  configured: boolean;
}

interface PaymentSettings {
  provider: string;
  providers: ProviderInfo[];
}

// Short marketing-ish blurb per provider id (UI only).
const PROVIDER_DESCRIPTION: Record<string, string> = {
  wayforpay: 'WayForPay — картки Visa/Mastercard, Apple Pay, Google Pay.',
  monobank: 'Monobank — оплата карткою через платіжну сторінку monobank.',
};

export const AdminSettingsPage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);

  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/admin/settings/payment');
      if (res.status === 403) {
        setError(t('admin:settingsForbidden'));
        return;
      }
      if (!res.ok) throw new Error(String(res.status));
      const data: PaymentSettings = await res.json();
      setSettings(data);
      setSelected(data.provider);
    } catch {
      setError(t('admin:settingsLoadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await authFetch('/api/admin/settings/payment', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: selected }),
      });
      if (!res.ok) {
        let message = t('admin:settingsSaveError');
        try {
          const body = await res.json();
          if (body?.message) message = body.message;
        } catch {
          /* ignore */
        }
        setError(message);
        return;
      }
      const data: PaymentSettings = await res.json();
      setSettings(data);
      setSelected(data.provider);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    } catch {
      setError(t('admin:settingsSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const btnPrimary =
    'rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50';

  const dirty = settings !== null && selected !== settings.provider;

  return (
    <div className="admin-page od-modern">
      <div className="admin-header">
        <div>
          <h1>{t('admin:settingsTitle')}</h1>
          <p className="admin-subtitle">{t('admin:settingsSubtitle')}</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-100 bg-white p-5" style={{ maxWidth: 640 }}>
        <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500">
          {t('admin:paymentProvider')}
        </h2>
        <p className="mb-4 text-sm text-slate-500">{t('admin:paymentProviderHint')}</p>

        {loading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}

        {!loading && settings && (
          <div className="flex flex-col gap-3">
            {settings.providers.map((p) => {
              const disabled = !p.configured;
              const active = selected === p.id;
              return (
                <label
                  key={p.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                    active ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200 bg-white hover:bg-slate-50'
                  } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <input
                    type="radio"
                    name="payment-provider"
                    className="mt-1"
                    value={p.id}
                    checked={active}
                    disabled={disabled}
                    onChange={() => setSelected(p.id)}
                  />
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-slate-900">
                      {p.label}
                      {!p.configured && (
                        <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                          {t('admin:providerNotConfigured')}
                        </span>
                      )}
                    </span>
                    <span className="text-sm text-slate-500">
                      {PROVIDER_DESCRIPTION[p.id] ?? ''}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
        {saved && <p className="mt-4 text-sm text-green-600">{t('admin:settingsSaved')}</p>}

        {!loading && settings && (
          <div className="mt-5 flex items-center gap-3">
            <button type="button" className={btnPrimary} disabled={saving || !dirty} onClick={save}>
              {saving ? t('admin:saving') : t('admin:save')}
            </button>
            {dirty && (
              <button
                type="button"
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
                disabled={saving}
                onClick={() => setSelected(settings.provider)}
              >
                {t('common:cancel')}
              </button>
            )}
          </div>
        )}
      </div>

      <SellerRequisitesSection />
    </div>
  );
};

// ---------------------------------------------------------------------------
// B2B selling-entity requisites (shown on рахунок-фактура / акт).
// One card per VAT mode: "vat" (ТОВ, з ПДВ) and "novat" (ФОП, без ПДВ).
// ---------------------------------------------------------------------------

interface Seller {
  id: string;
  displayName: string;
  legalName: string;
  edrpou: string;
  ipnVat?: string | null;
  isVatPayer: boolean;
  vatRate: number;
  legalAddress: string;
  iban: string;
  bankName: string;
  mfo?: string | null;
  director?: string | null;
  taxNote?: string | null;
  certificateNumber?: string | null;
  phone?: string | null;
}

const SELLER_FIELDS: { key: keyof Seller; labelKey: string; vatOnly?: boolean; novatOnly?: boolean }[] = [
  { key: 'displayName', labelKey: 'admin:sellerDisplayName' },
  { key: 'legalName', labelKey: 'admin:sellerLegalName' },
  { key: 'edrpou', labelKey: 'admin:sellerEdrpou' },
  { key: 'ipnVat', labelKey: 'admin:sellerIpnVat', vatOnly: true },
  { key: 'legalAddress', labelKey: 'admin:sellerLegalAddress' },
  { key: 'iban', labelKey: 'admin:sellerIban' },
  { key: 'bankName', labelKey: 'admin:sellerBankName' },
  { key: 'mfo', labelKey: 'admin:sellerMfo' },
  { key: 'certificateNumber', labelKey: 'admin:sellerCertificateNumber', vatOnly: true },
  { key: 'phone', labelKey: 'admin:sellerPhone' },
  { key: 'director', labelKey: 'admin:sellerDirector' },
  { key: 'taxNote', labelKey: 'admin:sellerTaxNote', novatOnly: true },
];

function SellerRequisitesSection() {
  const { t } = useTranslation(['admin', 'common']);
  const [original, setOriginal] = useState<Record<string, Seller>>({});
  const [edited, setEdited] = useState<Record<string, Seller>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch('/api/admin/settings/sellers');
        if (!res.ok) throw new Error(String(res.status));
        const data: Seller[] = await res.json();
        const map: Record<string, Seller> = {};
        data.forEach((s) => (map[s.id] = s));
        setOriginal(map);
        setEdited(JSON.parse(JSON.stringify(map)));
      } catch {
        setError(t('admin:settingsLoadError'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (id: string, key: keyof Seller, value: string) =>
    setEdited((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  const isDirty = (id: string) => JSON.stringify(original[id]) !== JSON.stringify(edited[id]);

  const save = async (id: string) => {
    setSavingId(id);
    setError(null);
    setSavedId(null);
    try {
      const res = await authFetch(`/api/admin/settings/sellers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(edited[id]),
      });
      if (!res.ok) {
        let message = t('admin:settingsSaveError');
        try {
          const body = await res.json();
          if (body?.message) message = body.message;
        } catch {
          /* ignore */
        }
        setError(message);
        return;
      }
      const saved: Seller = await res.json();
      setOriginal((prev) => ({ ...prev, [id]: saved }));
      setEdited((prev) => ({ ...prev, [id]: JSON.parse(JSON.stringify(saved)) }));
      setSavedId(id);
      window.setTimeout(() => setSavedId(null), 2500);
    } catch {
      setError(t('admin:settingsSaveError'));
    } finally {
      setSavingId(null);
    }
  };

  const btnPrimary =
    'rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50';

  return (
    <div className="mt-6 rounded-xl border border-slate-100 bg-white p-5" style={{ maxWidth: 720 }}>
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500">
        {t('admin:sellersTitle')}
      </h2>
      <p className="mb-4 text-sm text-slate-500">{t('admin:sellersHint')}</p>

      {loading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {!loading &&
        Object.values(edited).map((s) => (
          <div key={s.id} className="mb-4 rounded-xl border border-slate-200 p-4">
            <div className="mb-3 flex items-center gap-2">
              <span className="font-semibold text-slate-900">{s.displayName || s.id}</span>
              <span
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  s.isVatPayer ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {s.isVatPayer ? t('admin:sellerVatBadge', { rate: s.vatRate }) : t('admin:sellerNoVatBadge')}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {SELLER_FIELDS.filter(
                (f) => !(f.vatOnly && !s.isVatPayer) && !(f.novatOnly && s.isVatPayer),
              ).map((f) => (
                <label key={String(f.key)} className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-600">{t(f.labelKey)}</span>
                  <input
                    type="text"
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-400"
                    value={(s[f.key] as string) ?? ''}
                    onChange={(e) => setField(s.id, f.key, e.target.value)}
                  />
                </label>
              ))}
            </div>

            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                className={btnPrimary}
                disabled={savingId === s.id || !isDirty(s.id)}
                onClick={() => save(s.id)}
              >
                {savingId === s.id ? t('admin:saving') : t('admin:save')}
              </button>
              {savedId === s.id && <span className="text-sm text-green-600">{t('admin:settingsSaved')}</span>}
            </div>
          </div>
        ))}
    </div>
  );
}
